import { useEffect, useState } from 'react';
import {
  getNewsletterKpis,
  getNewsletterPosts,
  despublicarNewsletterPost,
  previewNewsletterEdicao,
} from '@/lib/api';

// ── tipos (espelham admin-api/routers/newsletter.py) ─────────────────────────

interface EdicaoKpi {
  id: number;
  numero: number;
  assunto: string;
  status: string;
  sent_at: string | null;
  enviados: number;
  entregues: number;
  bounces: number;
  opens: number;
  cliques: number;
  taxa_entrega_pct: number | null;
  open_rate_pct: number | null;
  ctr_pct: number | null;
  unsubscribes: number;
  ativados: number;
  ativacao_pct: number | null;
  clicantes: number;
  clicantes_caso: number;
  clicantes_compra: number;
  clicantes_caso_pct: number | null;
  clicantes_compra_pct: number | null;
}

interface VotoSemana {
  id: number;
  titulo: string;
  persona: string;
  likes: number;
  dislikes: number;
}

interface RankPersona {
  persona: string;
  posts: number;
  likes: number;
  dislikes: number;
}

interface SemanaLista {
  semana: string;
  novos: number;
  unsubs: number;
  total: number;
}

interface KpisData {
  disponivel: boolean;
  erro?: string;
  edicoes: EdicaoKpi[];
  votos_semana: VotoSemana[];
  ranking_personas: RankPersona[];
  crescimento_lista: SemanaLista[];
}

interface PostRow {
  id: number;
  slug: string;
  persona: string;
  titulo: string;
  status: string;
  published_at: string | null;
  criado_por: string;
  likes: number;
  dislikes: number;
  views: number;
  views_unicas: number;
}

const PERSONA_LABEL: Record<string, string> = {
  'heitor-calazans': 'Dr. Heitor Calazans',
  'ze-do-calculo': 'Zé do Cálculo',
  'marina-duarte': 'Marina Duarte',
  'dona-filo': 'Dona Filó',
};

const fmtData = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('pt-BR') : '—';
const pct = (v: number | null) => (v != null ? `${v}%` : '—');

function Card({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: string;
}) {
  return (
    <div className="rounded-xl border border-slate-200 p-4 bg-white">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-3xl font-bold ${accent ?? 'text-slate-800'}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

export default function NewsletterPage() {
  const [kpis, setKpis] = useState<KpisData | null>(null);
  const [posts, setPosts] = useState<PostRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewLabel, setPreviewLabel] = useState<string>('');
  const [busyPost, setBusyPost] = useState<number | null>(null);

  function reload() {
    setLoading(true);
    Promise.all([
      getNewsletterKpis().then((r) => setKpis(r.data)),
      getNewsletterPosts().then((r) =>
        setPosts(r.data?.disponivel ? r.data.posts : []),
      ),
    ])
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function abrirPreview(ed: EdicaoKpi) {
    try {
      const r = await previewNewsletterEdicao(ed.id);
      if (r.data?.disponivel) {
        setPreviewHtml(r.data.html || '<p>Edição sem HTML.</p>');
        setPreviewLabel(`#${r.data.numero} — ${r.data.assunto} (${r.data.status})`);
      }
    } catch {
      /* silencioso — padrão das outras abas */
    }
  }

  async function despublicar(p: PostRow) {
    if (
      !window.confirm(
        `Despublicar "${p.titulo}"? O post sai do blog, do sitemap e da próxima edição.`,
      )
    )
      return;
    setBusyPost(p.id);
    try {
      const r = await despublicarNewsletterPost(p.id);
      if (r.data?.ok) reload();
      else window.alert(`Não foi possível despublicar: ${r.data?.erro ?? '?'}`);
    } catch {
      window.alert('Não foi possível despublicar (erro de rede/permissão).');
    } finally {
      setBusyPost(null);
    }
  }

  if (loading && !kpis)
    return <div className="p-6 text-slate-400">Carregando…</div>;
  if (!kpis) return <div className="p-6 text-slate-400">Sem dados.</div>;
  if (!kpis.disponivel)
    return (
      <div className="p-6 text-rose-500">
        Indisponível: {kpis.erro} (migração scripts/migrate_newsletter.sql
        aplicada?)
      </div>
    );

  const ultima = kpis.edicoes.find((e) => e.status === 'sent');
  const lista = kpis.crescimento_lista;
  const listaAtual = lista.length ? lista[lista.length - 1].total : 0;
  const novos7d = lista.length ? lista[lista.length - 1].novos : 0;
  const rascunhos = kpis.edicoes.filter((e) => e.status === 'draft');

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-800">Newsletter</h1>
        <p className="text-sm text-slate-500">
          Edição semanal + blog das personas. Envio com List-Unsubscribe
          one-click; métricas de e-mail via webhook Resend (email_event).
        </p>
      </div>

      {/* KPIs topo */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card
          label="Lista opt-in"
          value={String(listaAtual)}
          accent="text-indigo-600"
          sub={`+${novos7d} na última semana`}
        />
        <Card
          label="Última edição enviada"
          value={ultima ? `#${ultima.numero}` : '—'}
          sub={ultima ? `${ultima.enviados} enviados · ${fmtData(ultima.sent_at)}` : 'nenhuma ainda'}
        />
        <Card
          label="Open rate (última)"
          value={ultima ? pct(ultima.open_rate_pct) : '—'}
          accent="text-emerald-600"
          sub={ultima ? `${ultima.opens} opens · CTR ${pct(ultima.ctr_pct)}` : undefined}
        />
        <Card
          label="Ativação pós-envio (última)"
          value={ultima ? pct(ultima.ativacao_pct) : '—'}
          accent="text-emerald-600"
          sub={
            ultima
              ? `${ultima.ativados} subiram caso/editaram em 7d · ${ultima.unsubscribes} unsubs`
              : 'upload ou edição em 7 dias'
          }
        />
      </div>

      {/* Tabela de edições */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 text-sm font-semibold text-slate-700">
          Edições ({kpis.edicoes.length})
          {rascunhos.length > 0 && (
            <span className="ml-2 text-xs font-normal text-amber-600">
              {rascunhos.length} rascunho(s) aguardando envio
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-400">
              <tr>
                <th className="px-4 py-2 text-left">Edição</th>
                <th className="px-3 py-2 text-center">Status</th>
                <th className="px-3 py-2 text-right">Enviados</th>
                <th className="px-3 py-2 text-right">Entregues</th>
                <th className="px-3 py-2 text-right">Bounces</th>
                <th className="px-3 py-2 text-right">Opens</th>
                <th className="px-3 py-2 text-right">Open rate</th>
                <th className="px-3 py-2 text-right">Cliques</th>
                <th className="px-3 py-2 text-right">CTR</th>
                <th className="px-3 py-2 text-right">Unsubs</th>
                <th className="px-3 py-2 text-right">Ativação 7d</th>
                <th className="px-3 py-2 text-right" title="Dos que CLICARAM na edição: quantos subiram caso em 7 dias após o 1º clique">Clique→Caso</th>
                <th className="px-3 py-2 text-right" title="Dos que CLICARAM na edição: quantos COMPRARAM créditos (Stripe) em 7 dias após o 1º clique">Clique→Compra</th>
                <th className="px-3 py-2 text-center">Preview</th>
              </tr>
            </thead>
            <tbody>
              {kpis.edicoes.map((e) => (
                <tr key={e.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 text-slate-700">
                    <b>#{e.numero}</b>{' '}
                    <span className="text-slate-500 text-xs">{e.assunto}</span>
                    <div className="text-xs text-slate-400">{fmtData(e.sent_at)}</div>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span
                      className={`text-xs rounded-full px-2 py-0.5 ${
                        e.status === 'sent'
                          ? 'bg-emerald-50 text-emerald-600'
                          : 'bg-amber-50 text-amber-600'
                      }`}
                    >
                      {e.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right text-slate-700">{e.enviados}</td>
                  <td className="px-3 py-2 text-right text-slate-600">{e.entregues}</td>
                  <td className={`px-3 py-2 text-right ${e.bounces ? 'text-rose-600 font-semibold' : 'text-slate-400'}`}>
                    {e.bounces}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-600">{e.opens}</td>
                  <td className="px-3 py-2 text-right font-semibold text-slate-800">
                    {pct(e.open_rate_pct)}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-600">{e.cliques}</td>
                  <td className="px-3 py-2 text-right text-slate-600">{pct(e.ctr_pct)}</td>
                  <td className={`px-3 py-2 text-right ${e.unsubscribes ? 'text-amber-600' : 'text-slate-400'}`}>
                    {e.unsubscribes}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold text-emerald-600">
                    {pct(e.ativacao_pct)}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-700">
                    {e.clicantes
                      ? `${e.clicantes_caso}/${e.clicantes} (${pct(e.clicantes_caso_pct)})`
                      : '—'}
                  </td>
                  <td className={`px-3 py-2 text-right font-semibold ${e.clicantes_compra ? 'text-emerald-700' : 'text-slate-400'}`}>
                    {e.clicantes
                      ? `${e.clicantes_compra}/${e.clicantes} (${pct(e.clicantes_compra_pct)})`
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button
                      type="button"
                      onClick={() => void abrirPreview(e)}
                      className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-100"
                    >
                      Ver
                    </button>
                  </td>
                </tr>
              ))}
              {kpis.edicoes.length === 0 && (
                <tr>
                  <td colSpan={12} className="px-4 py-4 text-center text-slate-400">
                    Nenhuma edição ainda — rode scripts/newsletter_weekly.py.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Preview em iframe */}
      {previewHtml !== null && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-700">
              Preview da edição {previewLabel}
            </div>
            <button
              type="button"
              onClick={() => setPreviewHtml(null)}
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              Fechar ✕
            </button>
          </div>
          <iframe
            title="Preview da edição"
            sandbox=""
            srcDoc={previewHtml}
            className="w-full bg-slate-100"
            style={{ height: 640, border: 0 }}
          />
        </div>
      )}

      {/* Conteúdo: votos da semana + ranking de personas + crescimento */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="text-sm font-semibold text-slate-700 mb-1">
            Votos da semana (por post)
          </h2>
          <p className="text-xs text-slate-400 mb-3">👍/👎 dos leitores nos últimos 7 dias.</p>
          <div className="space-y-2">
            {kpis.votos_semana.map((v) => (
              <div key={v.id} className="flex items-center gap-2 text-sm">
                <span className="flex-1 truncate text-slate-700" title={v.titulo}>
                  {v.titulo}
                </span>
                <span className="text-xs text-slate-400">
                  {PERSONA_LABEL[v.persona] ?? v.persona}
                </span>
                <span className="text-emerald-600 font-semibold">👍 {v.likes}</span>
                <span className="text-rose-500">👎 {v.dislikes}</span>
              </div>
            ))}
            {kpis.votos_semana.length === 0 && (
              <div className="text-sm text-slate-400">Sem votos esta semana.</div>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="text-sm font-semibold text-slate-700 mb-1">
            Ranking de personas
          </h2>
          <p className="text-xs text-slate-400 mb-3">Likes acumulados por colunista.</p>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-400">
              <tr>
                <th className="text-left py-1">Persona</th>
                <th className="text-right">Posts</th>
                <th className="text-right">👍</th>
                <th className="text-right">👎</th>
              </tr>
            </thead>
            <tbody>
              {kpis.ranking_personas.map((r) => (
                <tr key={r.persona} className="border-t border-slate-100">
                  <td className="py-1.5 text-slate-700">
                    {PERSONA_LABEL[r.persona] ?? r.persona}
                  </td>
                  <td className="text-right text-slate-600">{r.posts}</td>
                  <td className="text-right font-semibold text-emerald-600">{r.likes}</td>
                  <td className="text-right text-rose-500">{r.dislikes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="text-sm font-semibold text-slate-700 mb-1">
            Crescimento da lista (12 semanas)
          </h2>
          <p className="text-xs text-slate-400 mb-3">Novos opt-ins × unsubscribes × total.</p>
          <div className="space-y-1.5">
            {(() => {
              const max = Math.max(1, ...lista.map((s) => s.total));
              return lista.map((s) => (
                <div key={s.semana} className="flex items-center gap-2 text-xs">
                  <span className="w-20 text-slate-500">{s.semana}</span>
                  <div className="flex-1 bg-slate-100 rounded h-3.5 overflow-hidden">
                    <div
                      className="bg-indigo-400 h-3.5"
                      style={{ width: `${(100 * s.total) / max}%` }}
                    />
                  </div>
                  <span className="w-10 text-right text-slate-700 font-semibold">
                    {s.total}
                  </span>
                  <span className="w-16 text-right text-emerald-600">+{s.novos}</span>
                  <span className="w-10 text-right text-rose-500">
                    {s.unsubs ? `−${s.unsubs}` : ''}
                  </span>
                </div>
              ));
            })()}
            {lista.length === 0 && (
              <div className="text-sm text-slate-400">Sem opt-ins ainda.</div>
            )}
          </div>
        </div>
      </div>

      {/* Posts (com Despublicar) */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 text-sm font-semibold text-slate-700">
          Últimos posts ({posts?.length ?? 0})
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-400">
              <tr>
                <th className="px-4 py-2 text-left">Post</th>
                <th className="px-3 py-2 text-left">Persona</th>
                <th className="px-3 py-2 text-center">Status</th>
                <th className="px-3 py-2 text-left">Publicado</th>
                <th className="px-3 py-2 text-right">Visualizações</th>
                <th className="px-3 py-2 text-right">Únicas</th>
                <th className="px-3 py-2 text-right">👍</th>
                <th className="px-3 py-2 text-right">👎</th>
                <th className="px-3 py-2 text-center">Ação</th>
              </tr>
            </thead>
            <tbody>
              {(posts ?? []).map((p) => (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 text-slate-700 max-w-[340px]">
                    <a
                      href={`https://app.jusquant.ai/blog/${p.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                      title={p.slug}
                    >
                      {p.titulo}
                    </a>
                  </td>
                  <td className="px-3 py-2 text-slate-500 text-xs">
                    {PERSONA_LABEL[p.persona] ?? p.persona}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span
                      className={`text-xs rounded-full px-2 py-0.5 ${
                        p.status === 'published'
                          ? 'bg-emerald-50 text-emerald-600'
                          : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {p.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-400 text-xs">
                    {fmtData(p.published_at)}
                  </td>
                  <td
                    className={`px-3 py-2 text-right font-semibold ${
                      p.views ? 'text-indigo-600' : 'text-slate-300'
                    }`}
                  >
                    {p.views}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-500">
                    {p.views_unicas}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold text-emerald-600">
                    {p.likes}
                  </td>
                  <td className="px-3 py-2 text-right text-rose-500">{p.dislikes}</td>
                  <td className="px-3 py-2 text-center">
                    {p.status === 'published' && (
                      <button
                        type="button"
                        disabled={busyPost === p.id}
                        onClick={() => void despublicar(p)}
                        className="rounded-md border border-rose-200 px-2 py-1 text-xs text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                      >
                        {busyPost === p.id ? '…' : 'Despublicar'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {(posts ?? []).length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-4 text-center text-slate-400">
                    Nenhum post ainda — rode scripts/blog_daily.py.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
